import { authedGet, authedWrite } from './apiClient';
import { persistResource } from './store';
import { daysBetweenIso } from './civilDate';

export const SERVICE_COMPLIANCE_KEY = 'service-compliance';
export const ALERT_DAYS = 45;

export interface ServiceComplianceRow {
  id: number;
  locationCode: string;
  employeeId: number | null;
  fullName: string;
  position: string;
  hireDate: string | null;
  verificationType: string;
  verificationDetail: string;
  dateOrdered: string | null;
  dateConfirmed: string | null;
  renewalDate: string | null;
  expirationDate: string | null;
  dueDate: string | null;
  enteredBy: string;
  enteredAt: string;
}

export interface ServiceComplianceData {
  rows: ServiceComplianceRow[];
  alertDays: number;
}

export function alertDate(row: Pick<ServiceComplianceRow, 'dueDate' | 'expirationDate' | 'renewalDate'>): string | null {
  return row.dueDate || row.expirationDate || row.renewalDate || null;
}

export function daysUntilAlert(
  row: Pick<ServiceComplianceRow, 'dueDate' | 'expirationDate' | 'renewalDate'>,
  today: string,
): number | null {
  const when = alertDate(row);
  if (!when) return null;
  return daysBetweenIso(today, when);
}

export function isDueSoon(
  row: Pick<ServiceComplianceRow, 'dueDate' | 'expirationDate' | 'renewalDate'>,
  today: string,
  windowDays = ALERT_DAYS,
): boolean {
  const days = daysUntilAlert(row, today);
  return days != null && days <= windowDays;
}

export function isoToMd(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}

export function fetchServiceCompliance(): Promise<ServiceComplianceData> {
  return authedGet('/api/v1/company/service-compliance');
}

export type ServiceComplianceWrite = {
  locationCode: string;
  employeeId?: number | null;
  fullName: string;
  position: string;
  hireDate: string;
  verificationType: string;
  verificationDetail: string;
  dateOrdered: string;
  dateConfirmed: string;
  renewalDate: string;
  expirationDate: string;
  dueDate: string;
};

export async function addServiceCompliance(body: ServiceComplianceWrite): Promise<void> {
  await authedWrite('POST', '/api/v1/company/service-compliance', body);
}

export async function updateServiceCompliance(id: number, body: ServiceComplianceWrite): Promise<void> {
  await authedWrite('PATCH', `/api/v1/company/service-compliance/${id}`, body);
}

export async function deleteServiceCompliance(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/service-compliance/${id}`, {});
}

export async function reloadServiceCompliance(): Promise<void> {
  const data = await fetchServiceCompliance();
  await persistResource(SERVICE_COMPLIANCE_KEY, data);
}
